/* eslint-disable @angular-eslint/prefer-inject */
import { UserPositionDataService } from '../infrastructure/user-positions/user-position.data.service';
import {
    UserPositionCreate,
    UserPositionDto,
    UserPositionRead,
    UserPositionSearchParams,
} from '../entities/user-position.dto';
import { UserPositionStore } from '../infrastructure/user-positions/user-position.store';
import { inject, Injectable, InjectionToken, Injector } from '@angular/core';
import { RoutesForUserPositions } from './user-position.routes';
import { AbstractManageEntityFacade } from '@pf/shared-services';
import { UserPositionMapper } from '../infrastructure/user-positions/user-position.mapper';
import { BuildChangeTrackingFacadeFactory } from '@pf/shared/util-platform';
import {
    IPagedResult,
    ITypeEntity,
    ProfileProvider,
    SafeAny,
} from '@pf/shared-common';
import {
    BehaviorSubject,
    catchError,
    combineLatest,
    first,
    forkJoin,
    map,
    Observable,
    of,
    switchMap,
    tap,
} from 'rxjs';
import { UserPositionTypeFacade } from '../..';
import { asArraySafe } from '@pf/shared-utility';
import { ResourceAuth, UserPositionStatus } from '@control-tower/platform-core';
import {
    USER_POSITION_EXIT_CLEANUP_TASKS,
    UserPositionExitCleanupTask,
} from './user-position-exit.tokens';

export type UserPositionContextProvider = {
    getContext$: (positionType?: string) => Observable<object>;
};

export const userPositionContextProviderToken = new InjectionToken<
    UserPositionContextProvider | UserPositionContextProvider[]
>('user-position-context-provider');

@Injectable()
export class UserPositionFacade extends AbstractManageEntityFacade<
    UserPositionDto,
    UserPositionRead,
    UserPositionCreate,
    UserPositionSearchParams
> {
    loadingActive$ = new BehaviorSubject<boolean>(true);
    nameField: keyof UserPositionDto = 'name';
    private readonly contextProviders =
        inject(userPositionContextProviderToken, { optional: true }) ?? null;

    private readonly profileProvider = inject(ProfileProvider);
    private readonly positionTypeFacade = inject(UserPositionTypeFacade);
    private readonly injector = inject(Injector);

    override mergeOnUpdate = false;

    constructor(
        dataService: UserPositionDataService,
        routes: RoutesForUserPositions,
        store: UserPositionStore,
        mapper: UserPositionMapper
    ) {
        super(dataService, routes, store, mapper);
        this.listenForRealTimeEvents('user-positions', {
            processEventsIfNotInStore: true,
            customEntityMapping: (entity: UserPositionRead) => {
                if (entity.status === 'Exited') {
                    this.store.delete(entity);
                    return of(null);
                }
                return of(entity);
            },
        });

        this.loadingActive$.next(true);
        this.profileProvider.id$
            .pipe(
                switchMap(userId =>
                    this.search$({
                        pageSize: 1,
                        pageIndex: 1,
                        filters: {
                            userId,
                            status: UserPositionStatus.Active,
                        },
                    })
                ),
                tap((result: IPagedResult<UserPositionDto>) => {
                    if (result.data.length > 0) {
                        this.setActive(result.data[0].id);
                    }
                    this.loadingActive$.next(false);
                })
            )
            .subscribe();
    }

    changeTrackingFactory = BuildChangeTrackingFacadeFactory(
        this.dataService,
        this.nameField as SafeAny
    );

    textSearchFilter(
        searchText: string
    ): Partial<Record<keyof UserPositionDto, string>> {
        return { name: searchText };
    }

    override applyResourceAuth(
        body: UserPositionCreate,
        dto: UserPositionDto
    ): void {
        const ctx = dto.context as SafeAny;
        const locationId = ctx?.['locationId'] ?? dto.locationId;
        body.resourceAuth = {
            locations: locationId ? [locationId] : [],
        } as ResourceAuth;
    }

    setUserPosition$(positionType: string, context?: object) {
        return this._resolveContext$(positionType).pipe(
            switchMap(resolvedCtx =>
                this._getOrCreateActiveUserPosition$(positionType, {
                    ...resolvedCtx,
                    ...(context || {}),
                })
            ),
            tap(result => {
                this.setActive(result.id);
            })
        );
    }

    exitPosition$(
        entity?: UserPositionDto | null
    ): Observable<UserPositionDto | null> {
        if (!this.active) {
            return of(null);
        }
        const positionForCleanup = entity || (this.active as UserPositionDto);
        const updatedPosition = new UserPositionDto(positionForCleanup);
        updatedPosition.status = UserPositionStatus.Exited;

        const tasks =
            this.injector.get<UserPositionExitCleanupTask[] | null>(
                USER_POSITION_EXIT_CLEANUP_TASKS,
                null,
                { optional: true }
            ) ?? [];
        const applicable = tasks.filter(task =>
            task.appliesTo(positionForCleanup)
        );
        const cleanup$: Observable<unknown> = applicable.length
            ? forkJoin(
                  applicable.map(task =>
                      task.cleanup$(positionForCleanup).pipe(
                          catchError(err => {
                              this.logger.error(
                                  `exitPosition cleanup '${task.id}' failed`,
                                  err
                              );
                              return of(null);
                          })
                      )
                  )
              )
            : of(null);

        return cleanup$.pipe(
            switchMap(() => this.save$(updatedPosition)),
            tap(() => {
                if (!entity) {
                    this.resetActive();
                }
                const toDelete = this.store.get(updatedPosition.id);
                this.store.delete(toDelete);
            })
        );
    }

    updateContext$(context?: object): Observable<UserPositionDto | null> {
        if (!this.active) {
            return of(null);
        }

        return this._resolveContext$(this.active.userPositionTypeName).pipe(
            switchMap(resolvedCtx => {
                const updatedPosition = new UserPositionDto(
                    this.active as UserPositionDto
                );
                updatedPosition.context = {
                    ...(this.active?.context as object),
                    ...resolvedCtx,
                    ...(context || {}),
                };
                return this.save$(updatedPosition);
            }),
            tap(result => {
                this.setActive(result.id);
            })
        );
    }

    private _contextProviderList(): UserPositionContextProvider[] {
        const p = this.contextProviders;
        if (p == null) {
            return [];
        }
        return Array.isArray(p) ? p : [p];
    }

    private _resolveContext$(positionType?: string): Observable<object> {
        const providers = this._contextProviderList();
        if (providers.length === 0) {
            return of({});
        }
        return combineLatest(
            providers.map(p => p.getContext$(positionType))
        ).pipe(
            first(),
            map(contexts =>
                contexts.reduce((acc, curr) => ({ ...acc, ...curr }), {})
            )
        );
    }

    private _getTypeByName$(name: string): Observable<ITypeEntity | null> {
        return this.positionTypeFacade.getAll$().pipe(
            switchMap(types => {
                const found = asArraySafe(types).find(t => t.name === name);
                return of(found || null);
            })
        );
    }

    private _getOrCreateActiveUserPosition$(
        positionType: string,
        context: object
    ) {
        return this._getTypeByName$(positionType).pipe(
            switchMap(typeEntity => {
                if (!typeEntity) {
                    throw new Error(
                        `User position type not found: ${positionType}`
                    );
                }
                return this.save$(
                    new UserPositionDto({
                        userId: this.profileProvider.profile.userId,
                        locationId: (context as SafeAny)?.['locationId'],
                        userPositionTypeId: typeEntity.id,
                        userPositionTypeName: typeEntity.name,
                        status: 'Active',
                        context: context,
                    })
                );
            })
        );
    }
}
